/**
 * Portal Granel - Design System Tokens
 *
 * Centralized design tokens following design system best practices.
 * All components should import from this file instead of hardcoding values.
 *
 * Structure:
 * - Colors (semantic + utility)
 * - Spacing
 * - Typography
 * - Border Radius
 * - Shadows
 * - Transitions
 * - Z-index
 *
 * @example
 * import { colors, spacing, typography } from '@/design-system/tokens';
 *
 * const StyledButton = styled.button`
 *   background: ${colors.primary};
 *   padding: ${spacing.md};
 *   font-size: ${typography.fontSize.md};
 * `;
 */

// ============================================================================
// COLORS
// ============================================================================

/**
 * Semantic color palette mapped from legacy brand colors
 * These colors maintain the original Ultragaz brand identity
 */
export const colors = {
  // Primary brand colors
  primary: '#000eff', // mediumPurple - main brand color
  primaryDark: '#11117f', // darkPurple - darker variant
  primaryLight: '#4A3FD6', // indigo - lighter variant
  primaryHover: '#328cfd', // hover state

  // Secondary brand colors
  secondary: '#F0B01B', // darkYellow - accent color
  secondaryLight: '#FFF4D6', // light yellow background
  secondaryHover: '#D9A000', // darker yellow on hover

  // Status colors
  success: '#4EC937', // darkGreen - success/positive states
  successLight: '#E6F7E6', // light green background
  successBorder: '#4EC937',

  error: '#C10000', // red - error/danger states
  errorLight: '#FFE6E6', // light red background
  errorBorder: '#C10000',

  warning: '#F0B01B', // darkYellow - warning states
  warningLight: '#FFF4D6', // light yellow background
  warningBorder: '#F0B01B',

  info: '#106cff', // lightBlue - informational states
  infoLight: '#E0F7FA', // light blue background
  infoBorder: '#2180E8',

  // Neutral colors
  neutral: '#575757', // standard gray
  neutralLight: '#F0F0F0', // very light gray
  neutralBg: '#FFFFFF', // white background
  neutralBgDark: '#F5F5F5', // off-white background

  // Text colors
  text: '#575757', // primaryText - main text
  textSecondary: '#808495', // secondaryText - secondary text
  textContrast: '#262626', // dark text for contrast
  textInverse: '#FFFFFF', // text on dark backgrounds

  // Border colors
  border: '#E8E8E8', // default border
  borderLight: '#C1C1C1', // light gray border
  borderDark: '#7A7A7A', // dark gray border

  // Background colors
  background: '#FFFFFF', // main background
  backgroundContrast: '#000000', // pure black

  // Utility colors (legacy, preserved for backward compatibility)
  black: '#262626',
  black2: '#000000',
  white: '#FFFFFF',
  gray: '#7A7A7A',
  mediumGray: '#B4B4B4',
  lightGray: '#C1C1C1',
  lightGreen: '#28ff00',
  lightBlue: '#106cff',
  mediumBlue: '#2180E8',
  blue: '#000FFF',
  darkBlue: '#074F8C',
  blueClean: '#00ffff',
} as const;

// ============================================================================
// SPACING
// ============================================================================

/**
 * Consistent spacing scale using 4px base unit
 * Used for padding, margin, gaps, etc.
 */
export const spacing = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '20px',
  '2xl': '24px',
  '3xl': '32px',
  '4xl': '40px',
  '5xl': '48px',
} as const;

// ============================================================================
// TYPOGRAPHY
// ============================================================================

export const typography = {
  // Font family
  fontFamily: {
    primary: "'Mangueira', sans-serif",
    fallback: "system-ui, -apple-system, sans-serif",
  },

  // Font sizes
  fontSize: {
    xs: '11px',
    sm: '12px',
    md: '14px',
    lg: '16px',
    xl: '18px',
    '2xl': '20px',
    '3xl': '24px',
    '4xl': '32px',
  },

  // Font weights
  fontWeight: {
    regular: 400,
    medium: 500,
    bold: 600,
    extraBold: 700,
  },

  // Line heights
  lineHeight: {
    tight: 1.2,
    normal: 1.5,
    relaxed: 1.75,
  },

  // Predefined text styles
  h1: {
    fontSize: '32px',
    fontWeight: 700,
    lineHeight: 1.2,
    fontFamily: "'Mangueira', sans-serif",
  },
  h2: {
    fontSize: '24px',
    fontWeight: 700,
    lineHeight: 1.3,
    fontFamily: "'Mangueira', sans-serif",
  },
  h3: {
    fontSize: '18px',
    fontWeight: 600,
    lineHeight: 1.3,
    fontFamily: "'Mangueira', sans-serif",
  },
  h4: {
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: 1.4,
    fontFamily: "'Mangueira', sans-serif",
  },
  body: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: 1.5,
    fontFamily: "'Mangueira', sans-serif",
  },
  bodySmall: {
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: 1.5,
    fontFamily: "'Mangueira', sans-serif",
  },
  label: {
    fontSize: '12px',
    fontWeight: 600,
    lineHeight: 1.2,
    fontFamily: "'Mangueira', sans-serif",
  },
  caption: {
    fontSize: '11px',
    fontWeight: 400,
    lineHeight: 1.4,
    fontFamily: "'Mangueira', sans-serif",
  },
} as const;

// ============================================================================
// BORDER RADIUS
// ============================================================================

/**
 * Consistent border radius scale
 */
export const borderRadius = {
  sm: '4px',
  md: '6px',
  lg: '8px',
  xl: '12px',
  full: '9999px',
} as const;

// ============================================================================
// SHADOWS
// ============================================================================

/**
 * Consistent shadow scale for elevation
 */
export const shadows = {
  none: 'none',
  sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
  md: '0 2px 4px rgba(0, 0, 0, 0.08)',
  lg: '0 4px 8px rgba(0, 0, 0, 0.1)',
  xl: '0 8px 16px rgba(0, 0, 0, 0.12)',
  '2xl': '0 16px 24px rgba(0, 0, 0, 0.15)',
} as const;

// ============================================================================
// TRANSITIONS
// ============================================================================

/**
 * Consistent animation/transition timings
 */
export const transitions = {
  fast: '0.15s ease',
  base: '0.2s ease',
  slow: '0.3s ease',
} as const;

// ============================================================================
// Z-INDEX SCALE
// ============================================================================

/**
 * Consistent z-index values for layering
 */
export const zIndex = {
  base: 0,
  dropdown: 100,
  sticky: 200,
  fixed: 300,
  offcanvas: 400,
  modal: 500,
  popover: 600,
  tooltip: 700,
} as const;

// ============================================================================
// GRADIENTS
// ============================================================================

/**
 * Predefined gradients from legacy brand
 */
export const gradients = {
  vertical: 'linear-gradient(180deg, #76fafc, #47b4fc, #1e77fb, #0a59fb, #000FFF)',
  horizontal: 'linear-gradient(90deg, #76fafc, #389efb, #1e77fb, #0a59fb, #000FFF)',
  horizontalReverse:
    'linear-gradient(-90deg, #76fafc, #389efb, #1e77fb, #0a59fb, #000FFF)',
} as const;

// ============================================================================
// SEMANTIC COLOR COMBINATIONS (Commonly used together)
// ============================================================================

/**
 * Predefined color combinations for specific use cases
 * Useful for components that always use certain color combinations
 */
export const semanticColors = {
  // Button variants
  button: {
    primary: {
      bg: colors.primary,
      color: colors.textInverse,
      hover: colors.primaryHover,
    },
    secondary: {
      bg: colors.neutralBg,
      color: colors.primary,
      border: colors.primary,
      hover: 'rgba(0, 15, 255, 0.85)',
    },
    ghost: {
      bg: 'transparent',
      color: colors.primary,
      hover: 'rgba(0, 15, 255, 0.75)',
    },
  },

  // Badge variants
  badge: {
    primary: {
      bg: '#E8E6FF',
      color: colors.primaryLight,
    },
    success: {
      bg: colors.successLight,
      color: colors.success,
    },
    warning: {
      bg: colors.secondaryLight,
      color: colors.secondary,
    },
    danger: {
      bg: colors.errorLight,
      color: colors.error,
    },
    info: {
      bg: colors.infoLight,
      color: colors.info,
    },
    neutral: {
      bg: colors.neutralLight,
      color: colors.text,
    },
  },

  // Status badge variants
  statusBadge: {
    success: {
      bg: colors.successLight,
      color: colors.success,
      border: colors.successBorder,
    },
    warning: {
      bg: colors.secondaryLight,
      color: colors.secondary,
      border: colors.warningBorder,
    },
    error: {
      bg: colors.errorLight,
      color: colors.error,
      border: colors.errorBorder,
    },
    info: {
      bg: colors.infoLight,
      color: colors.info,
      border: colors.infoBorder,
    },
  },

  // Alert variants
  alert: {
    info: {
      bg: colors.infoLight,
      color: colors.info,
      border: colors.infoBorder,
    },
    success: {
      bg: colors.successLight,
      color: colors.success,
      border: colors.successBorder,
    },
    warning: {
      bg: colors.secondaryLight,
      color: colors.secondary,
      border: colors.warningBorder,
    },
    error: {
      bg: colors.errorLight,
      color: colors.error,
      border: colors.errorBorder,
    },
    neutral: {
      bg: colors.neutralBgDark,
      color: colors.text,
      border: colors.border,
    },
  },
} as const;

// ============================================================================
// COMPONENT SIZE PRESETS
// ============================================================================

/**
 * Standard sizing configurations for components
 */
export const componentSizes = {
  button: {
    sm: {
      height: spacing.xl,
      padding: `0 ${spacing.md}`,
      fontSize: typography.fontSize.sm,
      borderRadius: borderRadius.sm,
    },
    md: {
      height: '37px',
      padding: `0 ${spacing.lg}`,
      fontSize: typography.fontSize.md,
      borderRadius: borderRadius.md,
    },
    lg: {
      height: '44px',
      padding: `0 ${spacing.xl}`,
      fontSize: typography.fontSize.lg,
      borderRadius: borderRadius.md,
    },
  },

  badge: {
    sm: {
      padding: `${spacing.xs} ${spacing.sm}`,
      fontSize: typography.fontSize.xs,
      borderRadius: borderRadius.md,
    },
    md: {
      padding: `${spacing.xs} ${spacing.sm}`,
      fontSize: typography.fontSize.sm,
      borderRadius: borderRadius.lg,
    },
  },

  card: {
    sm: {
      minHeight: '72px',
      padding: spacing.lg,
      borderRadius: borderRadius.lg,
      fontSize: typography.fontSize.sm,
    },
    md: {
      minHeight: '88px',
      padding: spacing.lg,
      borderRadius: borderRadius.xl,
      fontSize: typography.fontSize.md,
    },
  },
} as const;

// ============================================================================
// TYPE DEFINITIONS (for TypeScript usage)
// ============================================================================

export type Color = typeof colors[keyof typeof colors];
export type Spacing = typeof spacing[keyof typeof spacing];
export type FontSize = typeof typography.fontSize[keyof typeof typography.fontSize];
export type FontWeight = typeof typography.fontWeight[keyof typeof typography.fontWeight];
export type BorderRadius = typeof borderRadius[keyof typeof borderRadius];
export type Shadow = typeof shadows[keyof typeof shadows];
export type ZIndex = typeof zIndex[keyof typeof zIndex];
export type Transition = typeof transitions[keyof typeof transitions];

export default {
  colors,
  spacing,
  typography,
  borderRadius,
  shadows,
  transitions,
  zIndex,
  gradients,
  semanticColors,
  componentSizes,
};
